<template>
  <div v-if="isLoggedIn" class="page">
    <div class="content-container card" :class="isPreviewMode ? 'pointer-events-none' : ''">
      <div class="cover-container">
        <v-img class="img-cover" aspect-ratio="2.88" :src="isProfileLoaded ? utility.getCoverImgSrc(this.$route.params.cardId, user.levelId ? user.levelId : '') : ''" @click="qrOverlayOpen = true">
          <template v-slot:placeholder>
            <div class="loader-dot"></div>
          </template>
        </v-img>
        <div v-if="VUE_APP_TYPE !== 'production'" class="cover-btn-container-top-left">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="rounded" elevation="3" fab x-small v-bind="attrs" v-on="on">
                {{ $i18n.locale }}
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item v-for="locale in ['th','en','jp']" :key="locale" @click="$i18n.locale = locale">
                <v-list-item-title>{{ locale.toUpperCase() }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="cover-btn-container">
          <template v-if="homeTheme === HOME_THEME.CLASSIC">
            <v-btn id="btn-qr-scan" elevation="3" fab small @click="showMethodOptionDialog">
              <icon-base :width="20" :height="20" :icon-color="$vuetify.theme ? $vuetify.theme.currentTheme.primary : null" viewBox="0 0 20 17" icon-name="btn-show-qr">
                <icon-scan-qr></icon-scan-qr>
              </icon-base>
            </v-btn>
            <v-btn class="ml-2" elevation="3" fab small @click="showQrDialog">
              <icon-base :width="20" :height="20" :icon-color="$vuetify.theme ? $vuetify.theme.currentTheme.primary : null" viewBox="0 0 20 20" icon-name="btn-show-qr">
                <icon-show-qr></icon-show-qr>
              </icon-base>
            </v-btn>
          </template>
          <template v-else-if="homeTheme === HOME_THEME.LEVEL_UP">
            <v-btn id="btn-qr-scan" class="rounded-lg" elevation="1" fab small @click="showMethodOptionDialog">
              <svg width="23" height="23" viewBox="0 0 19 19" xmlns="http://www.w3.org/2000/svg">
                <use :href="utility.getImgSrc('menu_icon/etc/qr-scan.svg') + '#Layer_1'"></use>
              </svg>
            </v-btn>
            <v-btn class="rounded-lg ml-2" elevation="1" fab small @click="showQrDialog">
              <svg width="23" height="23" viewBox="0 0 19 19" xmlns="http://www.w3.org/2000/svg">
                <use :href="utility.getImgSrc('menu_icon/etc/qr-show.svg') + '#Layer_1'"></use>
              </svg>
            </v-btn>
          </template>
        </div>
      </div>
      <div class="card-content" :class="homeTheme === HOME_THEME.LEVEL_UP ? 'background-secondary' : ''">
        <CardBlock v-if="homeTheme === HOME_THEME.CLASSIC" :loading="!isProfileLoaded" :card="card" :user="user" :token="token" :deviceId="deviceId" :uuid="uuid" />
        <CardPointBlockV2 v-else-if="homeTheme === HOME_THEME.LEVEL_UP" :loading="!isProfileLoaded" :card="card" :user="user" :token="token" :deviceId="deviceId" :uuid="uuid" />
        <CardPointBlockV2Alt v-else :loading="!isProfileLoaded" :card="card" :user="user" :token="token" :deviceId="deviceId" :uuid="uuid" @on-btn-qr-scan-click="showMethodOptionDialog" @on-btn-qr-show-click="showQrDialog" />
        <CouponBlockV2 v-if="homeTheme === HOME_THEME.BEE_HIVE" :card="card" :user="user" :token="token" :deviceId="deviceId" :uuid="uuid" />
        <ActionBlock v-if="homeTheme === HOME_THEME.CLASSIC" :isProfileLoading="!isProfileLoaded" :card="card" :user="user" />
        <ActionBlockV2 v-else :useHiveIcons="homeTheme === HOME_THEME.BEE_HIVE" :isProfileLoading="!isProfileLoaded" :card="card" :user="user" />
      </div>
    </div>
    <LogaFooter />
    <!-- QR code Dialog -->
    <user-qr-dialog
      :showDialog="qrDialogOpen"
      :token="token"
      :deviceId="deviceId"
      :uuid="uuid"
      :cardId="$route.params.cardId"
      @on-close="qrDialogOpen = false"
    />
    <!-- Code input Dialog -->
    <v-dialog v-model="methodDialogOpen" width="300">
      <v-card class="dialog-ticket">
          <v-card-title class="dialog-title primary text-lg">
            {{ $t('card.selectCodeInput') }}
          </v-card-title>
          <div class="dialog-content text-xl mt-4 mx-6">
            <v-radio-group v-model="methodOption" hide-details class="pt-0">
              <div v-for="o in methodOptionList" :key="o.id">
                <v-radio
                  :value="o.id"
                  class="mb-2"
                >
                  <template slot="label">
                    <span class="text--primary text-lg">{{ o.name }}</span>
                  </template>
                </v-radio>
              </div>
            </v-radio-group>
          </div>
          <v-card-actions class="dialog-actions d-block px-4 pb-4">
            <v-btn
              rounded
              depressed
              color="primary"
              class="btn-modal"
              @click="onMethodOptionConfirmClick"
            >
              {{ $t('app.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
    <!-- Ticket Dialog -->
    <v-dialog v-model="ticketDialogOpen" width="300">
      <v-card class="dialog-ticket">
          <v-card-title class="dialog-title primary text-lg">
            {{ $t('card.ticketQrCode') }}
          </v-card-title>
          <div class="dialog-content text-xl mt-4 mx-6">
            <v-text-field
              v-model="ticketNumber"
              dense single-line
              maxlength="16"
              :placeholder="$t('card.enterCode')"
              :rules="[v => isTicketNumberValid || $t('app.error.onlyTicketNumber')]"
            >
            </v-text-field>
          </div>
          <v-card-actions class="dialog-actions d-block px-4 pb-4">
            <v-btn
              v-if="!isTicketNumberValid"
              key="btn-1"
              rounded
              depressed
              color="primary"
              class="btn-modal"
              disabled
            >
              {{ $t('app.confirm') }}
            </v-btn>
            <v-btn
              v-else
              key="btn-2"
              rounded
              depressed
              color="primary"
              class="btn-modal"
              :loading="isCodeDialogSubmitting"
              @click="onTicketConfirmClick"
            >
              {{ $t('app.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
    </v-dialog>
    <!-- Modal: Show confirm after scanning -->
    <qr-confirm-dialog
      :showDialog="isQrConfirmDialogOpen"
      :qrCode="qrCode"
      :logaToken="logaToken"
      :uuid="uuid || deviceId"
      @on-close="isQrConfirmDialogOpen = false"
      @on-confirm="onQrConfirm"
    />
    <!-- Modal: Confirm using point ticket -->
    <point-ticket-confirm-dialog
      :showDialog="isPointTicketConfirmDialogOpen"
      :code="pointTicketCode"
      @on-close="isPointTicketConfirmDialogOpen = false"
      @on-confirm="onPointTicketConfirm"
    />
    <!-- Modal: Partner campaign result -->
    <v-dialog v-model="campaignDialogOpen" width="300">
      <v-card class="dialog-ticket">
        <v-card-title class="dialog-title primary text-lg">
          {{ campaignCouponId ? $t('campaign.successTitle') : $t('campaign.unavailableTitle') }}
        </v-card-title>
        <div class="dialog-content text-lg mt-4 mx-6 mb-5">
          <!-- The coupon's own artwork carries what it is: these are 3:1 banners with the offer
               written into them, which is why the LINE message for the same event leads with one
               and why the description underneath is not worth repeating here. -->
          <v-img
            v-if="campaignCouponImage"
            aspect-ratio="3"
            class="mb-3"
            style="width: 100%; border-radius: 5px;"
            :src="utility.getApiUrl() + campaignCouponImage"
          />
          {{ campaignCouponId ? $t('campaign.successMessage') : campaignDialogMessage }}
        </div>
        <v-card-actions class="dialog-actions d-block px-4 pb-4">
          <v-btn
            small
            rounded
            depressed
            text
            class="btn-modal text-transform-none mt-1"
            @click="campaignDialogOpen = false"
          >
            {{ $t('campaign.close') }}
          </v-btn>
          <v-btn
            v-if="campaignCouponId"
            rounded
            depressed
            color="primary"
            class="btn-modal-ok text-transform-none ml-0"
            @click="goToCampaignCoupon"
          >
            {{ $t('campaign.viewCoupon') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Modal: Show QR after success -->
    <qr-success-dialog
      :showDialog="isQrResultDialogOpen"
      :qrResult="qrResult"
      :item="scannedReward"
      @on-close="onQrSuccessDialogClose"
    />
    <!-- QR Reader -->
    <qr-reader v-if="qrScanOpen" @on-close="qrScanOpen = false" @on-decode="onQrReaderDecode" />
    <!-- QR code Overlay -->
    <user-qr-overlay :showOverlay="qrOverlayOpen" :token="token" :deviceId="deviceId" :uuid="uuid" :cardId="$route.params.cardId" @on-close="qrOverlayOpen = false" />
    <!-- shop delivery status popup -->
    <shop-delivery-status-popup
      v-if="card.shop_food"
      :token="token"
      :deviceId="deviceId"
      :uuid="uuid"
      :cardId="$route.params.cardId"
    />
  </div>
</template>

<script>
import { HOME_THEME } from '@/constants.js'
import liff from '@line/liff'
import LogaFooter from '@/components/LogaFooter.vue'

export default {
  name: 'Card',
  components: {
    LogaFooter,
    IconBase: () => import('@/components/IconBase.vue'),
    IconShowQr: () => import('@/components/icons/IconShowQr.vue'),
    IconScanQr: () => import('@/components/icons/IconScanQr.vue'),
    CardBlock: () => import('@/views/card-block/CardBlock.vue'),
    CardPointBlockV2: () => import('@/views/card-block/CardPointBlockV2.vue'),
    CardPointBlockV2Alt: () => import('@/views/card-block/CardPointBlockV2Alt.vue'),
    CouponBlockV2: () => import('@/views/card-block/CouponBlockV2.vue'),
    ActionBlock: () => import('@/views/card-block/ActionBlock.vue'),
    ActionBlockV2: () => import('@/views/card-block/ActionBlockV2.vue'),
    UserQrDialog: () => import('@/components/UserQrDialog.vue'),
    QrReader: () => import('@/components/QrReader.vue'),
    QrSuccessDialog: () => import('@/components/QrSuccessDialog.vue'),
    QrConfirmDialog: () => import('@/components/QrConfirmDialog.vue'),
    PointTicketConfirmDialog: () => import('@/components/PointTicketConfirmDialog.vue'),
    UserQrOverlay: () => import('@/components/UserQrOverlay.vue'),
    ShopDeliveryStatusPopup: () => import('@/views/shop/components/ShopDeliveryStatusPopup.vue')
  },
  data () {
    return {
      VUE_APP_TYPE: process.env.VUE_APP_TYPE,
      HOME_THEME: HOME_THEME,
      token: localStorage.getItem('token'),
      logaToken: sessionStorage.getItem('logaToken'),
      isPreviewMode: sessionStorage.getItem('isPreviewMode') === 'true',
      deviceId: localStorage.getItem('deviceId'),
      uuid: localStorage.getItem('deviceId') ? null : localStorage.getItem('uuid'),
      card: sessionStorage.getItem('card') ? JSON.parse(sessionStorage.getItem('card')) : {},
      isLoggedIn: sessionStorage.getItem('isLoggedIn') === 'true',
      user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : {},
      isProfileLoaded: !!localStorage.getItem('user'),
      qrDialogOpen: false,
      qrOverlayOpen: false,
      // qr options
      methodOptionList: [
        { id: 'qr', name: this.$t('card.inputQr') },
        { id: 'code', name: this.$t('card.ticketQrCode') }
      ],
      methodDialogOpen: false,
      methodOption: 'qr',
      // scan qr
      qrScanOpen: false,
      isQrConfirmDialogOpen: false,
      qrCode: null,
      isQrResultDialogOpen: false,
      qrResult: null,
      scannedCoupon: {},
      scannedReward: {},
      // point ticket
      isPointTicketConfirmDialogOpen: false,
      campaignDialogOpen: false,
      campaignDialogMessage: '',
      campaignCouponId: 0,
      campaignCouponImage: '',
      pointTicketCode: '',
      // ticket
      ticketNumber: null,
      ticketDialogOpen: false,
      isCodeDialogSubmitting: false,
      // for form submit to app
      formSubmitTime: null
    }
  },
  computed: {
    isTicketNumberValid: function () {
      return (/^\d+$/.test(this.ticketNumber)) && this.ticketNumber.length === 16
    },
    homeTheme: function () {
      if (this.card?.home_theme) {
        switch (this.card?.home_theme) {
          case 'legacy':
            return HOME_THEME.CLASSIC
          case 'levelup':
            return HOME_THEME.LEVEL_UP
          case 'hive':
            return HOME_THEME.BEE_HIVE
        }
      }
      return 1
    }
  },
  watch: {
    qrDialogOpen (val) {
      if (!val) this.getProfile()
    }
  },
  mounted () {
    const self = this
    const liffId = this.$route.query.liffId || localStorage.getItem('liffId')
    // const pageRedirect = this.$route.query['liff.state'] ? `/card/${this.$route.params.cardId}${this.$route.query['liff.state']}` : null
    const loginRedirect = sessionStorage.getItem('loginRedirect')
    if (liffId) {
      liff.ready.then(() => {
        const pageRedirect = self.$route.query.line_redirect ? `/card/${self.$route.params.cardId}/${self.$route.query.line_redirect}` : null
        if (!self.isLoggedIn) {
          if (!liff.isLoggedIn() && !liff.isInClient()) {
            if (pageRedirect) sessionStorage.setItem('loginRedirect', pageRedirect)
            console.log('pageRedirect: ' + pageRedirect)
            liff.login()
          } else {
            // skip login if there's already a token and deviceId
            if (self.token && !self.token.startsWith('temp-') && self.deviceId) {
              console.log('login skipped')
              sessionStorage.setItem('isLoggedIn', true)
              self.isLoggedIn = true
              self.init(loginRedirect || pageRedirect)
            } else {
              console.log('login not skipped')
              if (pageRedirect) sessionStorage.setItem('loginRedirect', pageRedirect)
              self.$router.replace({ name: 'LoginLine', params: { cardId: self.$route.params.cardId }, query: self.$route.query })
            }
          }
        } else {
          self.init(loginRedirect || pageRedirect)
        }
      }).catch((error) => {
        console.warn(error)
      })
    } else {
      if (self.token && !self.token.startsWith('temp-')) {
        sessionStorage.setItem('isLoggedIn', true)
        self.isLoggedIn = true
      }
      this.init(loginRedirect)
    }
  },
  methods: {
    init (pageRedirect) {
      const self = this
      sessionStorage.removeItem('loginRedirect')
      console.log('init:' + pageRedirect)
      if (pageRedirect) {
        this.checkConsentBeforeRedirect(pageRedirect)
      } else {
        this.initProfile().then(() => {
          self.afterInitProfile()
        })
      }
    },
    async initProfile () {
      await this.getProfile()
      if (!this.logaToken) {
        if (this.uuid) {
          this.logaToken = this.token
          sessionStorage.setItem('logaToken', this.token)
        } else await this.getLogaToken()
      }
    },
    showQrDialog () {
      this.qrDialogOpen = true
    },
    showMethodOptionDialog () {
      if (this.utility.isMobileNativeApp()) {
        window.location = '/scan_qr'
      } else {
        this.methodOption = this.methodOptionList[0].id // reset option
        this.methodDialogOpen = true
      }
    },
    scanQr () {
      const self = this
      if (liff.isInClient() && liff.scanCodeV2) {
        liff.scanCodeV2().then(result => {
          if (result.value && result.value !== 'null') self.processQRCodeResult(result.value)
        })
      } else {
        this.qrScanOpen = true
      }
    },
    onMethodOptionConfirmClick () {
      this.methodDialogOpen = false
      switch (this.methodOption) {
        case 'qr':
          this.scanQr()
          break
        case 'code':
          this.ticketNumber = null
          this.ticketDialogOpen = true
          break
      }
    },
    onTicketConfirmClick () {
      this.processQRCodeResult(this.ticketNumber, false, true)
    },
    processQRCodeResult (value, fromAndroid = false, fromDialog = false) {
      let code = value
      if (fromAndroid) {
        code = code.replaceAll('+', ' ')
        code = decodeURIComponent(code)
      }
      code = this.utility.getScannedCode(code)
      if (code) {
        if ((/^\d+$/.test(code)) && code.length === 16) {
          code = `TK:${code}`
        } else if (code.includes('/p/')) {
          const ticketNumber = code.split('/').pop()
          code = `TK:${ticketNumber}`
        }
        // check card id to make sure if it's in the correct card
        if (code.startsWith('ldcd://UP:') ||
          code.startsWith('ldcd://ybatqbpneq:UC:') ||
          code.startsWith('ldcd://UC:') ||
          code.startsWith('ldcd://ybatqbpneq:PC:') ||
          code.startsWith('ldcd://PC:')
        ) {
          const derivedCardId = parseInt(code.split(':')[code.startsWith('ldcd://ybatqbpneq:') ? 3 : 2].substring(1))
          if (derivedCardId !== parseInt(this.$route.params.cardId)) {
            this.showError(this.$t('card.codeCardError'))
          } else {
            this.showQrUseConfirmation(code)
          }
        } else {
          if (fromDialog) {
            this.useScannedCode(code, fromDialog)
          } else {
            this.showQrUseConfirmation(code)
          }
        }
      } else {
        this.showError(this.$t('card.codeFormatError'))
      }
    },
    onQrReaderDecode (value) {
      this.qrScanOpen = false
      this.processQRCodeResult(value)
    },
    showQrUseConfirmation (code) {
      this.qrCode = code
      this.isQrConfirmDialogOpen = true
    },
    onQrConfirm (remark) {
      this.useScannedCode(this.qrCode, false, remark)
    },
    onPointTicketConfirm () {
      this.usePointTicketWithoutMobile(this.pointTicketCode)
    },
    showTicketSuccessMessage (point, pointCardId) {
      if (pointCardId.startsWith('OL')) {
        pointCardId = pointCardId.substring(2)
      }
      // get card name
      this.api.getCardDetail({
        cardId: pointCardId,
        locale: this.$i18n.locale
      })
        .then(response => {
          switch (response.data.code) {
            case 200:
              this.dialogWarning.commit({
                type: 'show',
                message: this.$t('card.youGotPoint', {
                  point,
                  suffix: this.utility.getPluralSuffix(point, this.$i18n.locale),
                  cardName: response.data.data.name
                }),
                detail: null
              })
              break
          }
        }).catch((error) => {
          console.warn(error)
        }).finally(() => {
        })
    },
    showCreditSuccessMessage (amount, creditName, cardId) {
      // get card name
      this.api.getCardDetail({
        cardId,
        locale: this.$i18n.locale
      })
        .then(response => {
          switch (response.data.code) {
            case 200:
              this.dialogWarning.commit({
                type: 'show',
                message: this.$t('card.youUsedCredit', {
                  amount,
                  creditName,
                  cardName: response.data.data.name
                }),
                detail: null
              })
              break
          }
        }).catch((error) => {
          console.warn(error)
        }).finally(() => {
        })
    },
    // redirect to app to generate and save receipt image
    saveReceiptImage (type, receiptData) {
      this.formSubmitTime = '' + parseInt((new Date()).getTime() / 1000)
      if (typeof (Android) !== 'undefined') { // Android
        if (type === 'coupon') {
          // eslint-disable-next-line no-undef
          Android.saveCouponSlip(
            parseInt(this.formSubmitTime), this.scannedCoupon.detail,
            this.qrResult.code, this.scannedCoupon.coupon_id
          )
        } else {
          // eslint-disable-next-line no-undef
          Android.saveRewardSlip(
            parseInt(this.formSubmitTime), receiptData.name, receiptData.remark,
            this.qrResult.code, receiptData.reward_id, Math.abs(parseInt(receiptData.point_amount))
          )
        }
      } else if (window.webkit && window.webkit.messageHandlers) { // iOS
        if (type === 'coupon') {
          if (window.webkit.messageHandlers.saveCouponSlip) {
            window.webkit.messageHandlers.saveCouponSlip.postMessage({
              coupon_id: this.scannedCoupon.coupon_id,
              at: parseInt(this.formSubmitTime),
              coupon_detail: this.scannedCoupon.detail,
              transaction_id: this.qrResult.code
            })
          }
        } else {
          if (window.webkit.messageHandlers.saveRewardSlip) {
            window.webkit.messageHandlers.saveRewardSlip.postMessage({
              reward_id: receiptData.reward_id,
              amount: Math.abs(parseInt(receiptData.point_amount)),
              at: parseInt(this.formSubmitTime),
              reward_name: receiptData.name,
              reward_remark: receiptData.remark,
              transaction_id: this.qrResult.code
            })
          }
        }
      }
    },
    getClientInfo (lineDisplayName) {
      const ar = []
      if (liff.isInClient()) ar.push('line ' + liff.getLineVersion())
      else if (this.utility.isMobileNativeApp()) ar.push('app')
      else ar.push('browser')
      if (lineDisplayName) ar.push(lineDisplayName)
      ar.push(this.utility.getBrowserName())
      return ar.join(', ')
    },
    redirectToPage (loginRedirect) {
      const self = this
      const loginRedirectAr = loginRedirect.split('/')
      const redirectCode = loginRedirectAr.slice(3).join('/')
      if (redirectCode.startsWith('TK:')) {
        // remove line_redirect from url
        window.history.replaceState({}, '', this.$route.path)
        this.initProfile().then(() => {
          const qrCode = redirectCode
          self.useScannedCode(qrCode)
        })
      } else if (redirectCode.startsWith('Ticket:')) {
        // remove line_redirect from url
        window.history.replaceState({}, '', this.$route.path)
        this.initProfile().then(() => {
          const qrCode = redirectCode.split(':')[1]
          const pointTicketInfo = { code: qrCode }
          sessionStorage.setItem('pointTicketInfo', JSON.stringify(pointTicketInfo))
          self.afterInitProfile()
        })
      } else if (redirectCode.startsWith('Campaign:')) {
        // remove line_redirect from url
        window.history.replaceState({}, '', this.$route.path)
        this.initProfile().then(() => {
          sessionStorage.setItem('campaignCode', redirectCode.substring('Campaign:'.length))
          self.afterInitProfile()
        })
      } else if (redirectCode.startsWith('Mobile:')) {
        // remove line_redirect from url
        window.history.replaceState({}, '', this.$route.path)
        this.initProfile().then(() => {
          self.afterInitProfile()
        })
      } else if (redirectCode.startsWith('zeoniq')) {
        const decoded = redirectCode
        const paramStr = decoded.slice(decoded.indexOf('&') + 1)
        const params = new URLSearchParams(paramStr)
        this.$router.push({
          name: 'ZeoniqRedirect',
          params: {
            cardId: self.$route.params.cardId,
            callback_url: params.get('callback_url'),
            state: params.get('state'),
            membership_code: params.get('membership_code')
          }
        })
      } else {
        this.$router.replace({ path: loginRedirect })
      }
    },
    goToConsent () {
      const params = new URLSearchParams()
      params.append('token', this.token)
      if (this.uuid) params.append('uuid', this.uuid)
      else params.append('device_id', this.deviceId)
      params.append('card_id', this.$route.params.cardId)
      params.append('channel', 'L')
      params.append('client', this.getClientInfo())
      params.append('redirect', window.location.href)
      const url = this.api.apiBaseUrl + `privateweb/consent?${params.toString()}`
      window.location = url
    },
    onQrSuccessDialogClose () {
      const self = this
      this.isQrResultDialogOpen = false
      setTimeout(() => {
        self.scannedCoupon = {}
        self.scannedReward = {}
      }, 200)
    },
    onGetProfileErrorModalClose () {
      if (liff.isInClient()) {
        liff.closeWindow()
      } else {
        this.$router.go(-1)
      }
    },
    afterInitProfile () {
      if (sessionStorage.getItem('pointTicketInfo')) {
        if (this.isProfileLoaded) {
          const pointTicketInfo = JSON.parse(sessionStorage.getItem('pointTicketInfo'))
          this.pointTicketCode = pointTicketInfo.code
          this.isPointTicketConfirmDialogOpen = true
        }
        sessionStorage.removeItem('pointTicketInfo')
      }
      // Kept until the profile loads: a new member is sent to ProfileEdit first and comes back here.
      if (sessionStorage.getItem('campaignCode') && this.isProfileLoaded) {
        const campaignCode = sessionStorage.getItem('campaignCode')
        sessionStorage.removeItem('campaignCode')
        this.startCampaign(campaignCode)
      }
    },
    showError (message, onClose) {
      this.dialogWarning.commit({
        type: 'show',
        message,
        detail: null,
        onClose
      })
    },
    // ajax
    checkConsentBeforeRedirect (loginRedirect) {
      const self = this
      // this.loadingSpinner.commit('show')
      this.api.checkConsent({
        token: this.token,
        deviceId: this.deviceId,
        uuid: this.uuid,
        cardId: self.$route.params.cardId,
        channel: 'L', // L = LINE
        client: this.getClientInfo()
      })
        .then(response => {
          switch (response.data.code) {
            case 200:
              if (response.data.data === 'Y') {
                self.redirectToPage(loginRedirect)
              } else {
                self.goToConsent()
              }
              break
            case 252:
              if (this.utility.isMobileNativeApp()) {
                window.location = '/info'
              } else {
                this.$router.replace({ name: 'LoginLine', params: { cardId: this.$route.params.cardId } })
              }
              break
            default:
              if (response.data.msg) {
                this.showError(response.data.msg, this.onGetProfileErrorModalClose)
              }
              break
          }
        }).catch((error) => {
          console.warn(error)
          this.showError('Connection Error', this.onGetProfileErrorModalClose)
        }).finally(() => {
          // this.loadingSpinner.commit('hide')
        })
    },
    async getLogaToken () {
      if (this.isPreviewMode || !this.token) return
      try {
        const response = await this.api.getLogaToken({ locale: this.$i18n.locale, token: this.token, deviceId: this.deviceId, cardId: this.$route.params.cardId })
        switch (response.data.code) {
          case 200:
            this.logaToken = response.data.data.logaToken
            sessionStorage.setItem('logaToken', response.data.data.logaToken)
            break
          case 252:
          default:
            if (response.data.msg) {
              this.showError(response.data.msg, (event) => window.location.reload())
            }
            break
        }
      } catch (error) {
        console.warn(error)
        this.showError('Connection Error', (event) => window.location.reload())
      }
    },
    async getProfile () {
      // this.loadingSpinner.commit('show')
      const params = this.isPreviewMode ? { mock: true } : { locale: this.$i18n.locale, token: this.token, deviceId: this.deviceId, uuid: this.uuid, cardId: this.$route.params.cardId }
      if (!this.isPreviewMode && !this.token) {
        if (this.utility.isMobileNativeApp()) {
          window.location = '/info'
        } else {
          this.$router.replace({ name: 'LoginLine', params: { cardId: this.$route.params.cardId } })
        }
        return
      }
      try {
        const response = await this.api.getUserProfile(params)
        switch (response.data.code) {
          case 200:
            if (response.data.data.consent !== 'Y') {
              this.goToConsent()
            } else if (response.data.data.force_redirect2profile) {
              this.$router.replace({ name: 'ProfileEdit', params: { cardId: this.$route.params.cardId }, query: { new_member: 1 } })
            } else {
              this.user = response.data.data
              this.isProfileLoaded = true
              if (this.user) {
                localStorage.setItem('user', JSON.stringify(this.user))
              }
            }
            break
          case 252:
            if (this.utility.isMobileNativeApp()) {
              window.location = '/info'
            } else {
              this.$router.replace({ name: 'LoginLine', params: { cardId: this.$route.params.cardId } })
            }
            break
          case 403:
            break
          default:
            if (response.data.msg) {
              this.showError(response.data.msg, this.onGetProfileErrorModalClose)
            }
            break
        }
      } catch (error) {
        console.warn(error)
        this.showError('Connection Error', this.onGetProfileErrorModalClose)
      }
      // this.loadingSpinner.commit('hide')
    },
    useScannedCode (code, fromTicketDialog = false, remark = null) {
      if (fromTicketDialog) this.isCodeDialogSubmitting = true
      else this.loadingSpinner.commit('show')
      const codeData = { locale: this.$i18n.locale, logaToken: this.logaToken, uuid: this.uuid || this.deviceId, cardId: this.$route.params.cardId, code }
      if (remark) codeData.privateRemark = remark
      this.api.useScannedCode(codeData)
        .then(response => {
          if (response.data.code === 200 || response.data.result === 'Success') {
            if (fromTicketDialog) {
              this.ticketDialogOpen = false
            }
            this.getProfile()
            if (code.startsWith('TK:') && response.data.data && response.data.data.new && response.data.data.card_id) {
              // point ticket
              this.showTicketSuccessMessage(response.data.data.new, response.data.data.card_id)
            } else if (code.startsWith('ldcd://UD:')) {
              // use credit
              const codeArray = code.split(':')
              // amount, creditName, cardId
              this.showCreditSuccessMessage(codeArray[4], codeArray[5], parseInt(codeArray[2].substring(1)))
            } else if (code.startsWith('ldcd://ybatqbpneq:UC:') || code.startsWith('ldcd://UC:')) {
              // coupon
              const derivedCouponId = parseInt(code.split(':')[code.startsWith('ldcd://ybatqbpneq:UC:') ? 4 : 3])
              if (response.data.code) sessionStorage.setItem('couponTransactionId', response.data.code)
              this.$router.push({ name: 'CouponDetail', params: { cardId: this.$route.params.cardId, couponId: derivedCouponId } })
            } else if (code.startsWith('ldcd://UP:')) {
              // reward
              const codeArray = code.split(':')
              this.scannedReward = {
                reward_id: codeArray[3],
                name: codeArray[5],
                point_amount: -parseInt(codeArray[4]),
                display_amount: parseInt(codeArray[4]).toLocaleString()
              }
              if (response.data.result === 'Success') {
                this.qrResult = response.data
              } else {
                this.qrResult = {
                  result: 'Success',
                  code: response.data.data.transaction_id,
                  time: response.data.data.at,
                  redeemed_remark: response.data.data.redeemed_remark
                }
              }
              this.isQrResultDialogOpen = true
              this.saveReceiptImage('reward', {
                reward_id: parseInt(codeArray[3]),
                point_amount: parseInt(codeArray[4]),
                name: codeArray[5],
                remark: codeArray[5]
              })
            } else {
              // member ticket, etc.
              this.snackbar.commit({
                type: 'show',
                message: 'Success'
              })
            }
          } else {
            if (response.data.msg) {
              this.showError(response.data.msg)
            }
          }
        }).catch((error) => {
          console.warn(error)
          this.showError('Connection Error')
        }).finally(() => {
          if (fromTicketDialog) this.isCodeDialogSubmitting = false
          else this.loadingSpinner.commit('hide')
        })
    },
    // Partner campaign: read the campaign before asking anything of the person, then redeem.
    async startCampaign (code) {
      this.loadingSpinner.commit('show')
      try {
        const info = await this.api.getCampaignInfo({ locale: this.$i18n.locale, code })
        if (!info.data || info.data.code !== 200) {
          this.showError(info.data && info.data.msg ? info.data.msg : 'Unknown Error')
        } else {
          await this.redeemCampaign(code)
        }
      } catch (error) {
        console.warn(error)
        this.showError('Connection Error')
      } finally {
        this.loadingSpinner.commit('hide')
      }
    },
    async redeemCampaign (code) {
      try {
        const response = await this.api.redeemCampaign({
          locale: this.$i18n.locale,
          logaToken: this.logaToken,
          uuid: this.uuid || this.deviceId,
          code
        })
        if (response.data.code === 200) {
          const got = response.data.data || {}
          this.showCampaignResult('', got.coupon_id || 0, got.image || '')
        } else if (response.data.code === 252) {
          // Not a member of this card yet. Joining has rules of its own, so hand over to the
          // ordinary flow and pick the code up again when it returns here.
          sessionStorage.setItem('campaignCode', code)
          this.$router.push({ name: 'Join', params: { cardId: this.$route.params.cardId } })
        } else {
          this.showError(response.data.msg ? response.data.msg : 'Unknown Error')
        }
      } catch (error) {
        console.warn(error)
        this.showError('Connection Error')
      }
    },
    showCampaignResult (message, couponId, image) {
      this.campaignDialogMessage = message
      this.campaignCouponId = couponId
      this.campaignCouponImage = image || ''
      this.campaignDialogOpen = true
    },
    goToCampaignCoupon () {
      this.campaignDialogOpen = false
      this.$router.push({
        name: 'CouponDetail',
        params: { cardId: this.$route.params.cardId, couponId: this.campaignCouponId }
      })
    },
    usePointTicketWithoutMobile (code) {
      if (!code) return
      this.loadingSpinner.commit('show')
      this.api.usePointTicketWithoutMobile({
        locale: this.$i18n.locale,
        logaToken: this.logaToken,
        uuid: this.uuid || this.deviceId,
        code
      })
        .then(response => {
          switch (response.data.code) {
            case 200:
              if (response.data.data) {
                sessionStorage.setItem('pointTicketInfoSuccess', JSON.stringify(response.data.data))
                this.$router.push({ name: 'PointTicketSuccess', params: { cardId: this.$route.params.cardId } })
              }
              break
            default:
              this.showError(response.data.msg ? response.data.msg : 'Unknown Error')
          }
        }).catch((error) => {
          console.warn(error)
          this.showError('Connection Error')
        }).finally(() => {
          this.loadingSpinner.commit('hide')
        })
    }
  },
  // hooks
  beforeRouteEnter (to, from, next) {
    next(vm => {
      // support app callback
      window.showQrDialog = vm.showQrDialog
      window.processQRCodeResult = vm.processQRCodeResult
    })
  },
  beforeRouteLeave (to, from, next) {
    // attempt to fix randomly missing token
    if (this.token) {
      localStorage.setItem('token', this.token)
    }
    if (this.deviceId) {
      localStorage.setItem('deviceId', this.deviceId)
    } else if (this.uuid) {
      localStorage.setItem('uuid', this.uuid)
    }
    // attempt to fix randomly missing user
    if (this.user) {
      localStorage.setItem('user', JSON.stringify(this.user))
    }
    delete window.processQRCodeResult
    delete window.showQrDialog
    next()
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/style/card.scss';
</style>

<style lang="scss">
.v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
